// Tab "Bezug" (B1-B29 and the note of APP-PARITY.md): the next shot (quick choice, bean and recipe, pre-infusion, stop and
// target, fine tuning, tare, scale line), beans and grinder, the quick choice editor, the last shots with curve,
// comparison, taste, note, reference and deletion. Every change is one absolute set (AD-11).
import type { BeanItem, ShotItem } from '@brewcore/app-contract';
import type { CommandInput } from '../commands.js';
import {
  beansOf,
  formatPresets,
  goalOf,
  lagGrams,
  longestShot,
  manualActive,
  parsePresets,
  presetActive,
  presetCommands,
  presetFromValues,
  presetSummary,
  presetsWithTarget,
  ratioOf,
  stopModeOf,
  stopParams,
  type Bean,
  type Preset,
  type StopMode,
} from '../recipe.js';
import { dayFromIso, isoOfDay } from '../schedule.js';
import type { ShotsView } from '../shots.js';
import type { BeansView } from '../beans.js';
import { beanLabel, libraryRow, type LibraryRow } from './beans.js';
import { liveOf, opOf } from '../status.js';
import { typeset } from '../i18n.js';
import type { ParamValue } from '../store.js';
import { actionView, type ActionView, type ScreenContext } from './context.js';
import { controlView } from './settings.js';
import { curveData, shotBars, type CurveData } from './curves.js';
import { shotChart, t10Of, type ShotChartView } from './chart.js';
import { shotLanes, usualBand, type LaneChartView } from './lanes.js';
import {
  deviation,
  dotJoin,
  formatAuto,
  formatNumber,
  metaOf,
  offTarget,
  shotMs,
  shotTime,
  signed,
  stopModeText,
  whenText,
} from './format.js';
import {
  usualShots,
  rinseAction,
  shotFigures,
  usualCheck,
  usualNotes,
  tasteTagText,
  tasteView,
  type ShotFigures,
  type TasteView,
} from './machine.js';

/** A question before a change that is no machine command (deleting a shot). */
export interface AskView {
  title: string;
  text: string;
  ok: string;
  danger: boolean;
}

// ------------------------------------------------------------------------------------------------ next shot

export interface ScaleLineView {
  connected: boolean;
  text: string;
  battery: { text: string; low: boolean } | null;
}

/**
 * "Waage · 0,4 g" (paintShotLive() of the local app); the battery only when it needs charging (20.10.2026: the card had
 * too many figures). Null without a scale.
 */
export function scaleLine(ctx: ScreenContext): ScaleLineView | null {
  if (!ctx.scale) return null;
  const live = liveOf(ctx.view, ctx.now);
  const s = (ctx.view.groups.scale?.data ?? {}) as { status?: string; battery?: number | null };
  const connected = s.status === 'connected';
  const battery = connected ? (live?.battery ?? s.battery ?? null) : null;
  return {
    connected,
    text: connected
      ? `${ctx.tr('scaleOn')}${typeof live?.weight === 'number' ? ` · ${formatNumber(live.weight, 1, ctx.lang)} g` : ''}`
      : ctx.tr(s.status === 'none' ? 'scaleNone' : 'scaleOff'),
    battery:
      battery !== null && battery <= 20
        ? { text: `${ctx.tr('battery')} ${battery} %, ${ctx.tr('batteryLow')}`, low: true }
        : null,
  };
}

export interface StopRowView {
  label: string;
  mode: StopMode;
  modes: { value: StopMode; label: string; on: boolean }[];
  editable: boolean;
  /** "Nach Gewicht nur mit Waage. Höchstens 60 s" */
  hint: string;
}

export function stopRow(ctx: ScreenContext): StopRowView {
  const mode = stopModeOf(ctx.value, ctx.scale);
  const modes: StopMode[] = ['hand', 'time', ...(ctx.scale ? (['weight'] as StopMode[]) : [])];
  return {
    label: ctx.tr('stop'),
    mode,
    modes: modes.map((value) => ({
      value,
      label: ctx.tr(value === 'hand' ? 'stopHand' : value === 'time' ? 'stopByTime' : 'stopByWeight'),
      on: value === mode,
    })),
    editable: ctx.canEdit('brew.mode'),
    hint: `${ctx.scale ? '' : `${ctx.tr('stopHint')}. `}${ctx.tr('stopMax', { n: longestShot(ctx.value, ctx.scale) })}`,
  };
}

/** The stop mode as one set (brew.mode with both switches, APP-PARITY "Was die Firmware erfüllen muss" 2). */
export const stopCommand = (ctx: ScreenContext, mode: StopMode): CommandInput => ({
  kind: 'set',
  params: stopParams(mode, ctx.scale),
});

export interface NextShotView {
  title: string;
  /** "Bitte spülen" after a shot, with "Jetzt spülen" where the water level sensor allows it */
  rinse: { text: string; action: (ActionView & { rinsing: boolean }) | null } | null;
  pi: { on: boolean; label: string; hint: string | null };
  stop: StopRowView;
  targetTime: { label: string; hint: string | null } | null;
  targetWeight: { label: string; hint: string | null } | null;
  tare: ActionView | null;
  scale: ScaleLineView | null;
}

export function nextShotView(ctx: ScreenContext): NextShotView {
  const op = opOf(ctx.view);
  const live = liveOf(ctx.view, ctx.now);
  const mode = stopModeOf(ctx.value, ctx.scale);
  const piOn = ctx.value('brew.pre_infusion.enabled') === true;
  const ratio = ratioOf(ctx.num('brew.dose'), ctx.num('brew.by_weight.target_weight'));
  const scaleStatus = (ctx.view.groups.scale?.data as { status?: string } | undefined)?.status;
  const brewing = (live?.state ?? op.state) === 20;
  return {
    title: ctx.tr('sShot'),
    rinse:
      (live?.fp ?? op.flushHint)
        ? { text: ctx.tr('rinseNote'), action: ctx.watertank ? rinseAction(ctx) : null }
        : null,
    pi: {
      on: piOn,
      label: ctx.tr('sPreinf'),
      hint: piOn
        ? null
        : ctx.tr('piOn', {
            b: formatNumber(ctx.num('brew.pre_infusion.time'), 1, ctx.lang),
            p: formatNumber(ctx.num('brew.pre_infusion.pause'), 1, ctx.lang),
          }),
    },
    stop: stopRow(ctx),
    targetTime:
      mode === 'time' ? { label: ctx.tr('targetTime'), hint: piOn ? ctx.tr('afterPiInfo') : null } : null,
    targetWeight:
      mode === 'weight'
        ? {
            label: ctx.tr('targetWeight'),
            hint: ratio ? `${ctx.tr('ratio')} 1:${formatNumber(ratio, 1, ctx.lang)}` : null,
          }
        : null,
    tare: ctx.scale
      ? actionView(ctx, 'scale.tare', { label: 'tare', when: scaleStatus === 'connected' && !brewing })
      : null,
    scale: scaleLine(ctx),
  };
}

/** A changed target goes into the chosen quick choice in the same set (B10, saveTarget() of the local app). */
export function targetExtra(
  ctx: ScreenContext,
  id: 'brew.by_time.target_time' | 'brew.by_weight.target_weight',
): (value: number) => Record<string, ParamValue> {
  return (value) => {
    const next = presetsWithTarget(ctx.value('brew.presets'), ctx.value, ctx.scale, id, value);
    const extra: Record<string, ParamValue> = {};
    if (next) extra['brew.presets'] = next;
    return extra;
  };
}

export interface FineTuningView {
  title: string;
  lag: { label: string; hint: string } | null;
  fallbackTime: string | null;
  /** under "Bis zum Ende" (board "Feineinstellung"): "Temperatur bleibt … bei 93 °C." or "93 °C beim Start, 92 °C am Ende." */
  profileLine: string | null;
}

/** Fine tuning: the temperature curve always; the lag (with grams at the last flow) and the fallback time by weight. */
export function fineTuning(ctx: ScreenContext, shots: ShotsView): FineTuningView {
  const mode = stopModeOf(ctx.value, ctx.scale);
  const lastFlow =
    shots.items.map((s) => metaOf(s).flowAtStop).find((f): f is number => typeof f === 'number') ?? null;
  const lag = ctx.num('brew.by_weight.lag');
  return {
    title: ctx.tr('fine'),
    lag:
      mode === 'weight'
        ? {
            label: ctx.tr('lag'),
            // one line (UX line): what it means in grams at the last flow; the label says what it is
            hint: ctx.tr(lastFlow !== null ? 'lagLast' : 'lagAt', {
              f: formatNumber(lastFlow ?? 2, 1, ctx.lang),
              g: formatNumber(lagGrams(lag, lastFlow), 1, ctx.lang),
            }),
          }
        : null,
    fallbackTime: mode === 'weight' ? ctx.tr('fallbackTime') : null,
    profileLine: profileLine(ctx),
  };
}

function profileLine(ctx: ScreenContext): string | null {
  const sp = ctx.value('brew.setpoint');
  if (typeof sp !== 'number') return null;
  const end = ctx.num('brew.temp_end');
  const a = formatAuto(sp, ctx.lang);
  return end === 0
    ? ctx.tr('tempFlat', { t: a })
    : ctx.tr('tempRamp', { a, b: formatAuto(Math.round((sp + end) * 10) / 10, ctx.lang) });
}

// ------------------------------------------------------------------------------------------------ quick choice

/** What "Manuell" replaced, per browser (orione.manualBack of the local app); comes back with the next quick choice. */
export interface ManualBack {
  mode: StopMode;
  pi: boolean;
}

export const MANUAL_BACK_KEY = 'brewcore.manualBack';

export interface ChipsView {
  items: { index: number; name: string; sub: string; on: boolean }[];
  manual: { label: string; sub: string; on: boolean };
  /** what the chosen recipe sets besides its targets */
  info: string | null;
  enabled: boolean;
}

export function chipsView(ctx: ScreenContext): ChipsView {
  const dose = ctx.num('brew.dose', 18);
  const presets = parsePresets(ctx.value('brew.presets'), dose);
  const mode = stopModeOf(ctx.value, ctx.scale);
  const byWeight = mode === 'weight' || (mode === 'hand' && ctx.scale);
  const active = presets.findIndex((p) => presetActive(p, ctx.value, ctx.scale));
  const labels = { grind: ctx.tr('grindLabel'), pi: ctx.tr('sPreinf'), on: ctx.tr('on'), off: ctx.tr('off') };
  return {
    items: presets.map((p, index) => {
      const d = p.dose || dose;
      return {
        index,
        name: p.name,
        sub: byWeight
          ? `${formatAuto(d, ctx.lang)} → ${formatNumber(Math.round(d * p.r * 2) / 2, 1, ctx.lang)} g`
          : `${formatAuto(p.s, ctx.lang)} s`,
        on: index === active,
      };
    }),
    manual: { label: ctx.tr('manual'), sub: ctx.tr('manualSub'), on: manualActive(ctx.value, ctx.scale) },
    info: active >= 0 ? presetSummary(presets[active] as Preset, labels) || null : null,
    enabled: ctx.canEdit('brew.presets'),
  };
}

export interface PresetPlan {
  /** the library bean the quick choice names, chosen first with "Bohne wählen" (bean.put); null: no switch */
  select: string | null;
  /** the sets after it, sent one after the other while each is done */
  sets: Record<string, ParamValue>[];
}

/** The open beans of the library in the shape of a machine profile (name, stop, dose) for the quick choice. */
function libraryBeans(bv: BeansView | undefined): Bean[] {
  return (bv?.items ?? [])
    .filter((b) => !b.archivedAt)
    .map((b) => ({
      id: 0,
      name: b.name,
      ...(b.recipe?.recipe.stopMode ? { stopMode: b.recipe.recipe.stopMode } : {}),
      ...(typeof b.recipe?.recipe.dose === 'number' ? { dose: b.recipe.recipe.dose } : {}),
    }));
}

/** The open library bean with this name (case and spaces around do not matter), else any with it. */
export function libraryBeanNamed(bv: BeansView | undefined, name: string): string | null {
  const n = name.trim().toLowerCase();
  const items = bv?.items ?? [];
  const hit =
    items.find((b) => !b.archivedAt && b.name.trim().toLowerCase() === n) ??
    items.find((b) => b.name.trim().toLowerCase() === n);
  return hit?.id ?? null;
}

/**
 * A tap on a quick choice: first the bean through the library ("Bohne wählen": the machine gets exactly its recipe,
 * concept §2.5; a name the library does not know goes as set brew.beans like before), then one set with everything
 * else. A pre-infusion "Manuell" switched off comes back if the recipe keeps it.
 */
export function presetSteps(
  ctx: ScreenContext,
  index: number,
  back: ManualBack | null,
  bv?: BeansView,
): PresetPlan {
  const presets = parsePresets(ctx.value('brew.presets'), ctx.num('brew.dose', 18));
  const p = presets[index];
  if (!p) return { select: null, sets: [] };
  const library = libraryBeans(bv);
  const beans = library.length > 0 ? library : beansOf(ctx.view.groups.beans?.data).items;
  const steps = presetCommands(p, ctx.value, ctx.scale, beans, ctx.catalog);
  let select: string | null = null;
  const first = steps[0];
  if (steps.length > 1 && first && 'brew.beans' in first) {
    select = libraryBeanNamed(bv, String(first['brew.beans']));
    if (select) steps.shift();
  }
  if (back?.pi && p.pi < 0) {
    const last = steps.length - 1;
    steps[last] = { 'brew.pre_infusion.enabled': true, ...steps[last] };
  }
  return { select, sets: steps };
}

/** "Manuell": stop by hand, pre-infusion off, in one set; remembers what it replaced. Null if it is already manual. */
export function manualStep(
  ctx: ScreenContext,
): { params: Record<string, ParamValue>; remember: ManualBack } | null {
  if (manualActive(ctx.value, ctx.scale)) return null;
  return {
    params: { ...stopParams('hand', ctx.scale), 'brew.pre_infusion.enabled': false },
    remember: { mode: stopModeOf(ctx.value, ctx.scale), pi: ctx.value('brew.pre_infusion.enabled') === true },
  };
}

// ------------------------------------------------------------------------------------------------ beans

export interface BeanPickerView {
  label: string;
  /** names in the list: the open beans of the library, plus the machine's name while the library does not know it */
  names: string[];
  /** the bean chosen now (its name), '' = none */
  chosen: string;
  noneLabel: string | null;
  newLabel: string;
  enabled: boolean;
  roast: {
    label: string;
    age: string | null;
    iso: string;
    max: string;
    enabled: boolean;
    beanId: number;
  } | null;
}

/** The bean of the next shot from the library (concept §4 A: every open bean), the roast date of the current one. */
export function beanPicker(ctx: ScreenContext, bv?: BeansView): BeanPickerView {
  const beans = beansOf(ctx.view.groups.beans?.data);
  const current = beans.items.find((b) => b.id === beans.current) ?? null;
  const name = String(ctx.value('brew.beans') ?? '');
  const library = (bv?.items ?? []).filter((b) => !b.archivedAt);
  const names = library.length > 0 ? library.map((b) => b.name) : beans.items.map((b) => b.name);
  const has = (n: string) => names.some((x) => x.trim().toLowerCase() === n.trim().toLowerCase());
  if (name && !has(name)) names.unshift(name);
  const libraryCurrent = library.find((b) => b.current)?.name;
  return {
    label: ctx.tr('beans'),
    names,
    chosen: libraryCurrent ?? current?.name ?? name,
    noneLabel: name ? null : ctx.tr('beanNone'),
    newLabel: ctx.tr('beanNewOpt'),
    enabled: ctx.canEdit('brew.beans'),
    roast: current
      ? {
          label: ctx.tr('roast'),
          age:
            typeof current.roastDay === 'number'
              ? ctx.tr('roastAge', { n: Math.max(0, ctx.today - current.roastDay) })
              : null,
          iso: typeof current.roastDay === 'number' ? isoOfDay(current.roastDay) : '',
          max: isoOfDay(ctx.today),
          enabled: ctx.canEdit('bean.roast'),
          beanId: current.id,
        }
      : null,
  };
}

/** A new bean typed in the brew tab (quick create, concept §2.5): the machine creates it from the current values. */
export const chooseBean = (name: string): CommandInput => ({
  kind: 'set',
  params: { 'brew.beans': name.trim() },
});

/**
 * A bean picked in the list: a library bean goes with "Bohne wählen" (POST select: the machine gets exactly its recipe,
 * bean.put, or the way of the firmware before it); a name only the machine knows stays a set brew.beans.
 */
export function beanChoice(
  bv: BeansView | undefined,
  name: string,
): { select: string } | { command: CommandInput } {
  const id = libraryBeanNamed(bv, name);
  return id ? { select: id } : { command: chooseBean(name) };
}

/** A roast date from the date picker (the machine's local day); null for an empty or invalid value. */
export function roastCommand(beanId: number, iso: string): CommandInput | null {
  const day = dayFromIso(iso);
  return day === null ? null : { kind: 'action', action: 'bean.roast', args: { id: beanId, day } };
}

/** "Tag 9 · 18 g → 36 g · Mahlgrad 2.4 · 93 °C · PI" (short: the rows of the bean card and the library) */
export function beanSummary(ctx: ScreenContext, b: Bean): string {
  const n = (v: number, d?: number) =>
    d === undefined ? formatAuto(v, ctx.lang) : formatNumber(v, d, ctx.lang);
  return dotJoin([
    typeof b.roastDay === 'number' ? ctx.tr('dayN', { n: Math.max(0, ctx.today - b.roastDay) }) : '',
    b.dose
      ? `${n(b.dose)} g${b.targetWeight && b.stopMode === 'weight' ? ` → ${n(b.targetWeight, 1)} g` : ''}`
      : '',
    b.stopMode === 'time' && b.targetTime ? `${n(b.targetTime, 0)} s` : '',
    b.grind ? `${ctx.tr('grindLabel')}\u00a0${b.grind}` : '',
    b.setpoint
      ? `${n(b.setpoint, 1)} °C${b.tempEnd ? ` ${b.tempEnd > 0 ? '+' : '−'}${n(Math.abs(b.tempEnd), 1)} K` : ''}`
      : '',
    b.preinfusion?.on ? 'PI' : '',
  ]);
}

/** Rows of the beans card: the first open beans of the library, the rest behind "Alle ›". */
export const BEANS_CARD_MAX = 5;

export interface BeansCardView {
  rows: (LibraryRow & { current: boolean })[];
  /** open beans not shown in the card */
  more: number;
  empty: string | null;
  loading: boolean;
  error: string | null;
}

/** The beans card of the brew tab (concept §4 A): the library's open beans, the current one first; "Wählen" for the others. */
export function beansCard(ctx: ScreenContext, bv: BeansView): BeansCardView {
  const open = bv.items.filter((b) => !b.archivedAt);
  return {
    rows: open.slice(0, BEANS_CARD_MAX).map((b) => ({ ...libraryRow(ctx, b), current: b.current })),
    more: Math.max(0, open.length - BEANS_CARD_MAX),
    empty: bv.loaded && open.length === 0 ? ctx.tr('beansEmpty') : null,
    loading: !bv.loaded && bv.loading,
    error: bv.error,
  };
}

// ------------------------------------------------------------------------------------------------ quick choice editor

export interface PresetEditView {
  index: number;
  preset: Preset;
  summary: string;
  /** bean names to choose from ('' = keep the bean) */
  beanChoices: string[];
}

export function presetsEditor(
  ctx: ScreenContext,
  bv?: BeansView,
): {
  items: PresetEditView[];
  ratio: boolean;
  enabled: boolean;
} {
  const presets = parsePresets(ctx.value('brew.presets'), ctx.num('brew.dose', 18));
  // the open beans of the library (archived ones leave the editor, concept §2.5); the machine's while it loads
  const library = (bv?.items ?? []).filter((b) => !b.archivedAt).map((b) => b.name);
  const beans = library.length > 0 ? library : beansOf(ctx.view.groups.beans?.data).items.map((b) => b.name);
  const labels = { grind: ctx.tr('grindLabel'), pi: ctx.tr('sPreinf'), on: ctx.tr('on'), off: ctx.tr('off') };
  return {
    items: presets.map((preset, index) => ({
      index,
      preset,
      summary: presetSummary(preset, labels),
      beanChoices: [...beans, ...(preset.bean && !beans.includes(preset.bean) ? [preset.bean] : [])],
    })),
    ratio: ctx.scale,
    enabled: ctx.canEdit('brew.presets'),
  };
}

/** One recipe changed: the whole brew.presets as one set. Text fields lose the separators | and ;. */
export function savePreset(ctx: ScreenContext, index: number, patch: Partial<Preset>): CommandInput {
  const presets = parsePresets(ctx.value('brew.presets'), ctx.num('brew.dose', 18));
  const clean = (s: string | undefined) => (s === undefined ? undefined : s.replace(/[|;]/g, ' ').trim());
  const fixed: Partial<Preset> = { ...patch };
  if (patch.name !== undefined) fixed.name = clean(patch.name) || (presets[index]?.name ?? '');
  if (patch.grind !== undefined) fixed.grind = clean(patch.grind) ?? '';
  if (patch.bean !== undefined) fixed.bean = clean(patch.bean) ?? '';
  const list = presets.map((p, k) => (k === index ? { ...p, ...fixed } : p));
  return { kind: 'set', params: { 'brew.presets': formatPresets(list) } };
}

/** "Aktuelle Einstellungen übernehmen" for one recipe. */
export function takeCurrent(ctx: ScreenContext, index: number): CommandInput {
  const presets = parsePresets(ctx.value('brew.presets'), ctx.num('brew.dose', 18));
  const list = presets.map((p, k) => (k === index ? presetFromValues(p, ctx.value) : p));
  return { kind: 'set', params: { 'brew.presets': formatPresets(list) } };
}

/**
 * −/+ of a recipe number (one string for all three recipes, sent after 700 ms). With `unset` the value may be 0, "–":
 * the preset leaves it alone (dose, temperature). From "–" the first + starts at `unset` (the recipe's value now, as the
 * zeroStart of a row; 23.10.2026: from 0 with min 0 the + did nothing), − below min goes back to "–". Without it the
 * value stays within min and max (time, ratio).
 */
export function presetNumberStep(
  value: number,
  dir: 1 | -1,
  step: number,
  min: number,
  max: number,
  unset?: number,
): number {
  const clamp = (v: number) => Math.min(max, Math.max(min, Math.round(v / step) * step));
  if (unset !== undefined) {
    if (value === 0) return dir === 1 ? clamp(unset) : 0;
    if (dir === -1 && value - step < min) return 0;
  }
  return clamp((value === 0 && dir === 1 ? min - step : value) + dir * step);
}

export const PRESET_COMMIT_MS = 700;

// ------------------------------------------------------------------------------------------------ last shots

/**
 * A shot in a list (Letzte Bezüge, Verlauf): two figures, the taste at the right, one line under them (20.10.2026,
 * Dominik: "so viele zahlen dran, dass man direkt den überblick verliert"). Pre-infusion, ratio and the exact tenths
 * are on the shot's page; the deviation only when the shot missed its target.
 */
export interface ShotLineView extends ShotFigures {
  /** "29 s": whole seconds in a list, the shot's page has the tenths */
  timeShort: string;
  /** the rating as a word at the right of the row */
  taste: { text: string; good: boolean } | null;
  /** board "Verlauf": a shot off its target as a phrase under the figures ("3,2 g über dem Ziel"), else null */
  offText: string | null;
  /** kalt gestartet, von Hand gestoppt, aus der App, the taste tags (the rating is `taste`) */
  tags: string[];
}

export function shotLine(ctx: ScreenContext, shot: ShotItem): ShotLineView {
  const m = metaOf(shot);
  const rating =
    shot.rating === 1 ? 'sour' : shot.rating === 2 ? 'good' : shot.rating === 3 ? 'bitter' : null;
  const dev = deviation(m);
  return {
    ...shotFigures(ctx, shot),
    timeShort: `${formatNumber(shotTime(m).main, 0, ctx.lang)}\u00a0s`,
    taste: rating ? { text: ctx.tr(rating), good: rating === 'good' } : null,
    offText:
      dev && offTarget(dev)
        ? ctx.tr(dev.value > 0 ? 'devOver' : 'devUnder', {
            v: `${formatNumber(Math.abs(dev.value), dev.unit === 'g' ? 1 : 0, ctx.lang)}\u00a0${dev.unit}`,
          })
        : null,
    tags: [
      m.cold ? ctx.tr('coldStart') : '',
      m.stoppedByHand ? ctx.tr('handStop') : '',
      shot.source === 'app' ? ctx.tr('fromApp') : '',
      ...(shot.tags ?? []).map((t) => tasteTagText(ctx, t)),
    ].filter(Boolean),
  };
}

export interface ShotsCard {
  title: string;
  /** first page still loading */
  loading: boolean;
  empty: string | null;
  bars: { id: string; time: number; weight: number | null }[];
  legend: { time: string; weight: string | null };
  /** `sub`: when, and the bean only if it is not the current one (the tags are on the shot's page and in the history) */
  items: (ShotLineView & { sub: string })[];
  more: { label: string; enabled: boolean } | null;
  error: string | null;
}

/** The bars need a few shots to say anything (19.10.2026: one shot drew two bars over half the card). */
export const SHOT_BARS_MIN = 3;

export function shotsCard(ctx: ScreenContext, sv: ShotsView, beans?: BeansView): ShotsCard {
  const bars = sv.items.length >= SHOT_BARS_MIN ? shotBars(sv.items) : [];
  return {
    title: ctx.tr('sLast'),
    loading: !sv.loaded && sv.loading,
    empty: sv.loaded && sv.items.length === 0 ? ctx.tr('noShots') : null,
    bars,
    legend: {
      time: ctx.tr('shotTime'),
      weight: bars.some((b) => b.weight !== null) ? ctx.tr('shotWeight') : null,
    },
    items: sv.items.map((s) => {
      const line = shotLine(ctx, s);
      const current = beans?.items.find((b) => b.current)?.id;
      const other =
        s.beanId && s.beanId !== current
          ? (beans?.items.find((b) => b.id === s.beanId)?.name ?? metaOf(s).bean?.name ?? '')
          : '';
      return { ...line, sub: dotJoin([line.when, other]) };
    }),
    more: sv.next ? { label: ctx.tr('loadMore'), enabled: !sv.loading } : null,
    error: sv.error,
  };
}

/** One figure of an opened shot: label and value, a note after it (the reference's value, how it came about). */
export interface ShotFigure {
  label: string;
  value: string;
  note?: string;
}

export interface ShotDetailView {
  /**
   * The figures as the local app listed them, in groups (recipe, flow, temperature, stop) of label and value: one
   * scannable column instead of one long line (review 19.10.2026). The bean is in the shot's line above.
   */
  figures: ShotFigure[][];
  notes: string[];
  /** undefined while the curve loads, null if the shot has none */
  curve: CurveData | null | undefined;
  compare: { id: string | null; curve: CurveData | null; label: string | null };
  choices: { id: string; label: string; on: boolean }[];
  /** shots whose curves are needed for the comparison and not loaded yet */
  load: string[];
  targetWeight: number | null;
  /** the chart with scales, grid, stop, target, pre-infusion and the shot behind it (null without a curve) */
  chart: ShotChartView | null;
  taste: TasteView;
  isReference: boolean;
  referenceLabel: string;
  /** the bean whose reference this button sets (PUT …/beans/:beanId/reference); null: the period's (PUT …/reference) */
  referenceBean: string | null;
  deleteLabel: string;
  deleteAsk: AskView;
  /** prototype "Ein Bezug": the curve in three lanes with the usual range of the bean's last shots */
  lanes: LaneChartView | null;
  /** the app's reading against the usual range and the shot's own notes (channeling, by hand) */
  readings: { ok: boolean; text: string }[];
  phases: ShotPhase[];
  settings: { label: string; value: string }[];
}

/**
 * An opened shot: figures, hints (channeling, by hand, faster/slower than the reference), the curve with a comparison
 * (by default the reference, if it is another shot), taste, reference and deletion. `compare` undefined = default.
 */
export function shotDetail(
  ctx: ScreenContext,
  shot: ShotItem,
  sv: ShotsView,
  compare: string | null | undefined,
  bv?: BeansView,
): ShotDetailView {
  const m = metaOf(shot);
  const full = sv.curves[shot.id];
  // the reference of the shot's bean (reference per bean, 19.10.2026); a shot without a bean, or an API before
  // slice 5, keeps the one of the period
  const bean = shot.beanId ? bv?.items.find((b) => b.id === shot.beanId) : undefined;
  const ofBean = !!bean && 'referenceShotId' in bean;
  const refId = ofBean ? (bean.referenceShotId ?? null) : sv.reference.shotId;
  const refShot = refId
    ? (sv.curves[refId] ?? (refId === sv.reference.shotId ? (sv.reference.shot ?? undefined) : undefined))
    : undefined;
  const isRef = refId === shot.id;
  const cmpId = compare === undefined ? (refId && refId !== shot.id ? refId : null) : compare;
  const cmpShot = cmpId ? (cmpId === refId ? refShot : sv.curves[cmpId]) : undefined;
  const ratio = m.dose && typeof m.weight === 'number' ? ratioOf(m.dose, m.weight) : null;
  const n = (v: number, d: number) => formatNumber(v, d, ctx.lang);
  const r1 = (v: number) => Math.round(v * 10) / 10;
  const curve = full === undefined ? undefined : curveData(full.curve);
  const refMeta = refShot ? metaOf(refShot) : null;
  const refCurve = refShot ? curveData(refShot.curve) : null;
  // against the reference as the local app (refCompare): 10 g sooner, else the first drops, else the brew time
  const t10 = t10Of(curve);
  const refT10 = !isRef ? t10Of(refCurve) : null;
  const refFd = !isRef && typeof refMeta?.firstDrop === 'number' ? refMeta.firstDrop : null;
  const refDiff =
    !refMeta || isRef
      ? null
      : t10 !== null && refT10 !== null
        ? r1(r1(t10) - r1(refT10))
        : typeof m.firstDrop === 'number' && refFd !== null
          ? r1(m.firstDrop - refFd)
          : refMeta.duration && m.duration
            ? r1(m.duration - refMeta.duration)
            : null;
  const refNote = (v: number | null) => (v !== null ? `${ctx.tr('refShort')} ${n(v, 1)} s` : null);
  const pi = m.preinfusion && m.preinfusion.burst + m.preinfusion.pause > 0 ? m.preinfusion : null;
  const weighed = typeof m.weight === 'number' ? m.weight : null;
  const dev =
    m.stopMode === 'weight' && m.targetWeight && weighed !== null ? r1(weighed - m.targetWeight) : null;
  const fig = (label: string, value: string | null | undefined, note?: string | null): ShotFigure | null =>
    value ? { label, value: typeset(value), ...(note ? { note: typeset(note) } : {}) } : null;
  const group = (...xs: (ShotFigure | null)[]): ShotFigure[] => xs.filter((x): x is ShotFigure => x !== null);
  const figures = [
    group(
      fig(ctx.tr('dose'), m.dose ? `${n(m.dose, 1)} g` : null),
      fig(ctx.tr('ratio'), ratio ? `1:${n(ratio, 1)}` : null),
      fig(ctx.tr('grindLabel'), m.grind),
    ),
    group(
      fig(
        ctx.tr('sPreinf'),
        pi ? ctx.tr('piOn', { b: n(pi.burst, 1), p: n(pi.pause, 1) }) : null,
        pi?.valveOpen === false ? ctx.tr('piClosed') : null,
      ),
      fig(
        ctx.tr('piAfter'),
        pi && m.duration ? `${n(Math.max(0, m.duration - pi.burst - pi.pause), 1)} s` : null,
      ),
      fig(
        ctx.tr('firstDropAt'),
        typeof m.firstDrop === 'number' ? `${n(m.firstDrop, 1)} s` : null,
        refNote(refFd),
      ),
      fig(ctx.tr('t10'), t10 !== null ? `${n(t10, 1)} s` : null, refNote(refT10)),
    ),
    group(
      fig(ctx.tr('shotSetpoint'), typeof m.setpoint === 'number' ? `${n(m.setpoint, 1)} °C` : null),
      fig(
        ctx.tr('shotProfile'),
        typeof m.tempEnd === 'number' && m.tempEnd !== 0 ? `${signed(m.tempEnd, ctx.lang)} K` : null,
      ),
      fig(ctx.tr('startTemp'), typeof m.startTemp === 'number' ? `${n(m.startTemp, 1)} °C` : null),
    ),
    group(
      fig(ctx.tr('shotStop'), stopModeText(m, ctx.lang)),
      fig(
        ctx.tr('goal'),
        m.targetWeight && m.stopMode === 'weight'
          ? `${n(m.targetWeight, 1)} g`
          : m.targetTime && m.stopMode === 'time'
            ? `${n(m.targetTime, 0)} s`
            : null,
        dev !== null ? `${dev > 0 ? '+' : dev < 0 ? '−' : '±'}${n(Math.abs(dev), 1)} g` : null,
      ),
      fig(
        ctx.tr('atStop'),
        typeof m.stopWeight === 'number' ? `${n(m.stopWeight, 1)} g` : null,
        typeof m.lag === 'number' && typeof m.flowAtStop === 'number'
          ? ctx.tr('figLead', { g: n(m.lead ?? 0, 1), s: n(m.lag, 2), f: n(m.flowAtStop, 1) })
          : null,
      ),
      // what ran into the cup after the pump stopped
      fig(
        ctx.tr('drip'),
        typeof m.stopWeight === 'number' && weighed !== null
          ? `${n(Math.max(0, weighed - m.stopWeight), 1)} g`
          : null,
      ),
    ),
  ].filter((g) => g.length > 0);
  const notes = [
    m.channeling ? ctx.tr('chNote') : '',
    m.stoppedByHand ? ctx.tr('handStopNote') : '',
    refDiff !== null && Math.abs(refDiff) >= 1.5
      ? ctx.tr(refDiff < 0 ? 'fasterRef' : 'slowerRef', { s: n(Math.abs(refDiff), 1) })
      : '',
  ].filter(Boolean);
  const others = sv.items.filter((x) => x.id !== shot.id).slice(0, 3);
  const when = whenText(shotMs(shot), ctx.lang, ctx.now);
  const load = [
    ...new Set([
      ...(full === undefined ? [shot.id] : []),
      ...(cmpId && cmpShot === undefined ? [cmpId] : []),
      ...(refId && refId !== shot.id && refShot === undefined ? [refId] : []),
    ]),
  ];
  const cmpCurve = cmpShot ? curveData(cmpShot.curve) : null;
  // the usual range: the curves of the shot's usual shots (usualShots, loaded like the comparison)
  const earlier = usualShots(shot, sv.items);
  const earlierCurves = earlier
    .map((x) => sv.curves[x.id])
    .map((x) => (x ? curveData(x.curve) : null))
    .filter((x): x is CurveData => x !== null);
  for (const x of earlier) if (!sv.curves[x.id] && !load.includes(x.id)) load.push(x.id);
  const usual = usualBand(earlierCurves);
  const u = usualCheck(shot, sv.items);
  const readings = [
    ...usualNotes(ctx, u),
    ...(m.channeling ? [{ ok: false, text: ctx.tr('chNote') }] : []),
    ...(m.stoppedByHand ? [{ ok: false, text: ctx.tr('handStopNote') }] : []),
  ];
  const cmpLabel = cmpId
    ? cmpId === refId
      ? ctx.tr('refChip')
      : cmpShot
        ? whenText(shotMs(cmpShot), ctx.lang, ctx.now)
        : ctx.tr('compareWith')
    : null;
  const targetWeight = m.stopMode === 'weight' ? (m.targetWeight ?? null) : null;
  return {
    figures,
    notes,
    curve,
    compare: { id: cmpId, curve: cmpCurve, label: cmpLabel },
    choices: [
      ...(refId && refId !== shot.id ? [{ id: refId, label: ctx.tr('refChip'), on: cmpId === refId }] : []),
      ...others
        .filter((o) => o.id !== refId)
        .map((o) => ({ id: o.id, label: whenText(shotMs(o), ctx.lang, ctx.now), on: cmpId === o.id })),
    ],
    load,
    targetWeight,
    chart: curve ? shotChart(ctx, curve, cmpCurve, { targetWeight, pi, otherLabel: cmpLabel }) : null,
    taste: tasteView(ctx, shot),
    isReference: isRef,
    referenceLabel: isRef
      ? ctx.tr('refClear')
      : ofBean
        ? ctx.tr('refSetFor', { n: bean.name })
        : ctx.tr('refSet'),
    referenceBean: ofBean ? bean.id : null,
    lanes: curve
      ? shotLanes(ctx, curve, {
          firstDrop: typeof m.firstDrop === 'number' ? m.firstDrop : null,
          piSeconds: pi ? pi.burst + pi.pause : 0,
          targetWeight,
          usual,
          timeText: `${formatNumber(shotTime(m).main, 0, ctx.lang)} s`,
        })
      : null,
    readings,
    phases: shotPhases(ctx, shot, curve),
    settings: shotSettings(ctx, shot),
    deleteLabel: ctx.tr('deleteShot'),
    // the question as the title, the button with the same verb (WRITING.md 2: "Bezug löschen?" → "Löschen")
    deleteAsk: {
      title: ctx.tr('deleteShotQ'),
      text: ctx.tr('reallyDelete', { s: when }),
      ok: ctx.tr('deleteGo'),
      danger: true,
    },
  };
}

// ------------------------------------------------------------------------------------------------ prototype "Ein Bezug"

export interface ShotPhase {
  key: 'pi' | 'brew' | 'drip';
  name: string;
  value: string;
  hint: string;
  /** its share of the whole shot (the bar) */
  fraction: number;
}

/**
 * The phases of a shot (prototype "Ein Bezug"): until the first drop (pre-infusion, when it was on), brewing until
 * the pump stopped with its average flow, what ran into the cup after it.
 */
export function shotPhases(ctx: ScreenContext, shot: ShotItem, d: CurveData | null | undefined): ShotPhase[] {
  const m = metaOf(shot);
  const n = (v: number, digits: number) => formatNumber(v, digits, ctx.lang);
  const total = m.duration ?? 0;
  if (total <= 0) return [];
  const pi = m.preinfusion && m.preinfusion.burst + m.preinfusion.pause > 0 ? m.preinfusion : null;
  const drop = typeof m.firstDrop === 'number' && m.firstDrop > 0 ? m.firstDrop : null;
  const start = drop ?? (pi ? pi.burst + pi.pause : null);
  const stopS = d && d.stop >= 0 ? (d.stop * d.dt) / 1000 : total;
  const out: ShotPhase[] = [];
  if (start !== null)
    out.push({
      key: 'pi',
      name: ctx.tr(pi ? 'sPreinf' : 'phaseStart'),
      value: `${n(start, 1)}\u00a0s`,
      // without pre-infusion the name already says it; with it, until the first drop or its burst and pause
      hint: !pi
        ? ''
        : drop
          ? ctx.tr('phaseToDrop')
          : ctx.tr('piOn', { b: n(pi.burst, 1), p: n(pi.pause, 1) }),
      fraction: start / total,
    });
  const flows = (d?.flow ?? []).filter((v): v is number => v !== null && v > 0.3);
  const avg = flows.length ? flows.reduce((a, b) => a + b, 0) / flows.length : null;
  const steady = flows.length > 4 && Math.max(...flows.slice(2)) - Math.min(...flows.slice(2)) < 0.8;
  const brew = Math.max(0, stopS - (start ?? 0));
  out.push({
    key: 'brew',
    name: ctx.tr('phaseBrew'),
    value: `${n(brew, 1)}\u00a0s`,
    hint:
      avg === null
        ? m.stoppedByHand
          ? ctx.tr('handStop')
          : ''
        : ctx.tr(m.channeling ? 'phaseFlowUneven' : steady ? 'phaseFlowSteady' : 'phaseFlowAvg', {
            f: n(avg, 1),
          }),
    fraction: brew / total,
  });
  if (typeof m.stopWeight === 'number' && typeof m.weight === 'number')
    out.push({
      key: 'drip',
      name: ctx.tr('drip'),
      value: `${n(Math.max(0, m.weight - m.stopWeight), 1)}\u00a0g`,
      hint: ctx.tr('phaseDripHint', { g: n(m.stopWeight, 1) }),
      fraction: Math.max(0.02, (total - stopS) / total),
    });
  return out;
}

/** "Einstellungen dieses Bezugs" (prototype): what the shot was made with, two columns of name and value. */
export function shotSettings(ctx: ScreenContext, shot: ShotItem): { label: string; value: string }[] {
  const m = metaOf(shot);
  const n = (v: number, digits: number) => formatNumber(v, digits, ctx.lang);
  const pi = m.preinfusion && m.preinfusion.burst + m.preinfusion.pause > 0 ? m.preinfusion : null;
  return [
    m.dose ? { label: ctx.tr('dose'), value: `${n(m.dose, 1)} g` } : null,
    m.grind ? { label: ctx.tr('grindLabel'), value: m.grind } : null,
    typeof m.setpoint === 'number'
      ? {
          label: ctx.tr('shotSetpoint'),
          value: `${formatAuto(m.setpoint, ctx.lang)} °C${typeof m.tempEnd === 'number' && m.tempEnd !== 0 ? ` ${signed(m.tempEnd, ctx.lang)} K` : ''}`,
        }
      : null,
    typeof m.startTemp === 'number' ? { label: ctx.tr('startTemp'), value: `${n(m.startTemp, 1)} °C` } : null,
    stopModeText(m, ctx.lang) ? { label: ctx.tr('shotStop'), value: stopModeText(m, ctx.lang) } : null,
    m.stopMode === 'weight' && typeof m.stopWeight === 'number'
      ? { label: ctx.tr('atStop'), value: `${n(m.stopWeight, 1)} g` }
      : m.stopMode === 'weight' && m.targetWeight
        ? { label: ctx.tr('goal'), value: `${n(m.targetWeight, 1)} g` }
        : m.stopMode === 'time' && m.targetTime
          ? { label: ctx.tr('goal'), value: `${n(m.targetTime, 0)} s` }
          : null,
    pi ? { label: ctx.tr('sPreinf'), value: ctx.tr('piOn', { b: n(pi.burst, 1), p: n(pi.pause, 1) }) } : null,
  ]
    .filter((x): x is { label: string; value: string } => x !== null)
    .map((x) => ({ label: x.label, value: typeset(x.value) }));
}

/** The title of a shot's page: "Heute, 07:25", "Gestern, 18:16", "18.10., 08:41". */
export function shotTitle(ctx: ScreenContext, shot: ShotItem): string {
  const w = whenText(shotMs(shot), ctx.lang, ctx.now);
  const i = w.lastIndexOf(' ');
  const t = i > 0 ? `${w.slice(0, i)}, ${w.slice(i + 1)}` : w;
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** The shots before and after this one in the list (older and newer), for the arrows of its page. */
export function shotNeighbours(
  id: string,
  items: readonly ShotItem[],
): { older: string | null; newer: string | null } {
  const i = items.findIndex((s) => s.id === id);
  if (i < 0) return { older: null, newer: null };
  return { older: items[i + 1]?.id ?? null, newer: items[i - 1]?.id ?? null };
}

/** A comparison chip tapped: the chosen one again turns the comparison off. */
export const compareAfterTap = (current: string | null, tapped: string): string | null =>
  current === tapped ? null : tapped;

/** The note as typed: no control characters except line breaks, at most 500 characters (app-v1). */
export const cleanNote = (text: string): string =>
  // eslint-disable-next-line no-control-regex -- removing control characters is the point
  text.replace(/[\u0000-\u0009\u000b-\u001f\u007f]/g, '').slice(0, 500);

/** The note to save: trimmed, empty = no note. */
export const noteToSave = (draft: string): string | null => (draft.trim() ? draft.trim() : null);

// ------------------------------------------------------------------------------------------------ recipe summary

/**
 * The next shot at a glance (Bezug and Maschine): the bean with its day after roasting, then two rows of three figures,
 * each with its name above (20.10.2026, Dominik: "die anordnung der zahlen echt nicht übersichtlich"; before, a big
 * "18,0 g → 36,0 g" without names stood over a row in another style). Row one is what goes in and out (dose, stop,
 * ratio by weight), row two how (grind, temperature, pre-infusion); a missing ratio leaves its place empty, so the
 * columns never move. Changing it is the recipe page.
 */
export interface RecipeFact {
  key: 'dose' | 'stop' | 'ratio' | 'grind' | 'temp' | 'pi';
  label: string;
  value: string;
}

export interface RecipeSummaryView {
  bean: string;
  /** "Tag 11" of the current bean's bag, null without a roast date */
  day: string | null;
  /** six places, row by row; null = an empty place (no ratio without a target weight) */
  facts: (RecipeFact | null)[];
}

export function recipeSummary(ctx: ScreenContext): RecipeSummaryView {
  const beans = beansOf(ctx.view.groups.beans?.data);
  const current = beans.items.find((b) => b.id === beans.current) ?? null;
  const name = String(ctx.value('brew.beans') ?? '').trim();
  const mode = stopModeOf(ctx.value, ctx.scale);
  const dose = ctx.value('brew.dose');
  const weight = ctx.num('brew.by_weight.target_weight');
  const time = ctx.num('brew.by_time.target_time');
  const grind = String(ctx.value('brew.grind') ?? '').trim();
  const setpoint = ctx.value('brew.setpoint');
  const byWeight = mode === 'weight' && weight > 0;
  const ratio = byWeight && typeof dose === 'number' ? ratioOf(dose, weight) : null;
  const n = (v: number, unit: string) => `${formatAuto(v, ctx.lang)}\u00a0${unit}`;
  return {
    bean: name || ctx.tr('noBean'),
    day:
      current && typeof current.roastDay === 'number'
        ? ctx.tr('dayN', { n: Math.max(0, ctx.today - current.roastDay) })
        : null,
    // a value that is not known leaves its place empty (WRITING.md 0a: no "Mahlgrad –"); the columns stay
    facts: [
      typeof dose === 'number' && dose > 0
        ? { key: 'dose', label: ctx.tr('dose'), value: n(dose, 'g') }
        : null,
      byWeight || (mode === 'time' && time > 0) || mode === 'hand'
        ? {
            key: 'stop',
            label: ctx.tr('shotStop'),
            value: byWeight ? n(weight, 'g') : mode === 'time' ? n(time, 's') : ctx.tr('stopHand'),
          }
        : null,
      ratio
        ? {
            key: 'ratio',
            label: ctx.tr('ratio'),
            value: `1:${formatAuto(Math.round(ratio * 10) / 10, ctx.lang)}`,
          }
        : null,
      grind ? { key: 'grind', label: ctx.tr('grindLabel'), value: grind } : null,
      typeof setpoint === 'number'
        ? { key: 'temp', label: ctx.tr('brewTemp'), value: n(setpoint, '°C') }
        : null,
      {
        key: 'pi',
        label: ctx.tr('sPreinf'),
        value: ctx.tr(ctx.value('brew.pre_infusion.enabled') === true ? 'on' : 'off'),
      },
    ],
  };
}

/** Rows of "Letzte Bezüge" on the tab's main page; every other shot is in the history. */
export const SHOTS_MAIN = 5;

/**
 * About how much coffee is left in a bean's open bag (prototype "noch etwa 180 g"): its weight less the dose of every
 * shot from it, in steps of 10 g; null without a weight or while the list may not hold all of its shots.
 */
export function bagLeftG(bean: BeanItem | undefined, sv: ShotsView | undefined): number | null {
  const bag = bean?.bag;
  if (!bag?.weightG || !sv) return null;
  const from = sv.items.filter((s) => s.bagId === bag.id);
  const oldest = sv.items.at(-1);
  // the list goes back past the bag's start, or it is the whole history
  const complete = !sv.next || (oldest !== undefined && shotMs(oldest) < bag.createdAt);
  if (!complete) return null;
  const used = from.reduce((a, s) => a + (metaOf(s).dose ?? 0), 0);
  return Math.max(0, Math.round((bag.weightG - used) / 10) * 10);
}

/** The rows to the seldom-used pages of the tab: beans, presets, grinder, each with what it holds now. */
export function brewMoreView(
  ctx: ScreenContext,
  bv: BeansView,
  sv?: ShotsView,
): { beans: string; presets: string; grinder: string } {
  const open = bv.items.filter((b) => !b.archivedAt);
  const currentBean = open.find((b) => b.current);
  const current = currentBean ? beanLabel(currentBean) : undefined;
  const left = bagLeftG(currentBean, sv);
  const presets = parsePresets(ctx.value('brew.presets'), ctx.num('brew.dose', 18));
  const grinder = String(ctx.value('brew.grinder') ?? '').trim();
  const scale = controlView(ctx, 'brew.grind_scale');
  const scaleText = scale && typeof scale.value === 'number' && scale.value > 0 ? scale.text : '';
  return {
    // a list that did not load says so on its page, not in this one-line summary (23.10.2026: the whole error twice)
    beans: bv.error
      ? ''
      : !bv.loaded
        ? '…'
        : open.length === 0
          ? ctx.tr('beansEmpty')
          : dotJoin([
              current ?? '',
              left !== null ? ctx.tr('bagLeft', { g: left }) : ctx.tr('beansOpenN', { n: open.length }),
            ]),
    presets: presets.map((p) => p.name).join(', '),
    grinder: dotJoin([grinder, scaleText]) || ctx.tr('grinderNone'),
  };
}

// ------------------------------------------------------------------------------------------------ prototype "Rezept"

/** The preset chosen last on this browser (it stays chosen while its values are changed), per machine. */
export const LAST_PRESET_KEY = 'brewcore.lastPreset';

export interface RecipePresetsView {
  options: { value: number | 'manual'; label: string; on: boolean }[];
  /** the preset the recipe came from: the active one, else the one chosen last; null: none */
  chosen: number | 'manual' | null;
  /** "Geändert gegenüber Espresso" when the values differ from the chosen preset */
  changed: string | null;
  /** "Als Espresso speichern" with it */
  saveLabel: string | null;
  enabled: boolean;
}

/**
 * The presets over the recipe (prototype "Rezept"): the one in use stays chosen while its values are changed; then a
 * line says so with "Zurücksetzen", and "Als … speichern" at the end keeps the change in the preset.
 */
export function recipePresets(ctx: ScreenContext, last: number | null): RecipePresetsView {
  const chips = chipsView(ctx);
  const active = chips.items.find((c) => c.on)?.index ?? null;
  const chosen: number | 'manual' | null =
    active ??
    (chips.manual.on ? 'manual' : last !== null && last >= 0 && last < chips.items.length ? last : null);
  const changed = active === null && typeof chosen === 'number';
  const name = typeof chosen === 'number' ? (chips.items[chosen]?.name ?? '') : '';
  return {
    options: [
      ...chips.items.map((c) => ({
        value: c.index as number | 'manual',
        label: c.name,
        on: c.index === chosen,
      })),
      { value: 'manual', label: chips.manual.label, on: chosen === 'manual' },
    ],
    chosen,
    changed: changed ? ctx.tr('changedFrom', { p: name }) : null,
    saveLabel: changed ? ctx.tr('saveAsPreset', { p: name }) : null,
    enabled: chips.enabled,
  };
}

export interface AmountView {
  mode: StopMode;
  /** "1:2,0" by weight, null otherwise */
  ratioText: string | null;
  ratio: number | null;
  /** "Stoppt bei 36,0 g" / "Stoppt nach 25 s"; null by hand */
  stop: { label: string; value: string } | null;
  editable: boolean;
}

/** "Menge" (prototype "Rezept"): dose and ratio, the weight it stops at follows from them. */
export function amountView(ctx: ScreenContext): AmountView {
  const mode = stopModeOf(ctx.value, ctx.scale);
  const dose = ctx.num('brew.dose');
  const target = ctx.num('brew.by_weight.target_weight');
  const ratio = mode === 'weight' ? ratioOf(dose, target) : null;
  const time = ctx.num('brew.by_time.target_time');
  return {
    mode,
    ratio,
    ratioText: ratio !== null ? `1:${formatNumber(ratio, 1, ctx.lang)}` : null,
    stop:
      mode === 'weight' && target > 0
        ? { label: ctx.tr('stopsAt'), value: `${formatNumber(target, 1, ctx.lang)}\u00a0g` }
        : mode === 'time' && time > 0
          ? { label: ctx.tr('stopsAfter'), value: `${formatAuto(time, ctx.lang)}\u00a0s` }
          : null,
    editable: ctx.canEdit('brew.by_weight.target_weight'),
  };
}

/** A tap on −/+ of the ratio: 0.1 more or less, the target weight at it (0.5 g steps, within the catalog's range). */
export function ratioStepTarget(ctx: ScreenContext, ratio: number, dir: 1 | -1): number {
  const dose = ctx.num('brew.dose', 18) || 18;
  const next = Math.min(4, Math.max(1, Math.round((ratio + dir * 0.1) * 10) / 10));
  return goalOf(next, dose);
}

/** "2 s Stoß, dann 4 s Pause" under the pre-infusion's switch. */
export const piHint = (ctx: ScreenContext): string =>
  ctx.tr('piHint', {
    b: formatAuto(ctx.num('brew.pre_infusion.time'), ctx.lang),
    p: formatAuto(ctx.num('brew.pre_infusion.pause'), ctx.lang),
  });

// ------------------------------------------------------------------------------------------------ prototype "Schnellwahl"

/**
 * A preset in the list (prototype "Schnellwahl"): its name, what it does in one line ("18 g → 36 g · 93 °C ·
 * Pre-Infusion aus", "18 g · 30 s · 1:2,5 · 93 °C"), and "Zuletzt genutzt" over the one chosen last.
 */
export function presetRows(
  ctx: ScreenContext,
  last: number | null,
): { index: number; name: string; summary: string; tag: string | null }[] {
  const dose = ctx.num('brew.dose', 18);
  const presets = parsePresets(ctx.value('brew.presets'), dose);
  const mode = stopModeOf(ctx.value, ctx.scale);
  const byWeight = ctx.scale && mode !== 'time';
  const n = (v: number) => formatAuto(v, ctx.lang);
  return presets.map((p, index) => {
    const d = p.dose || dose;
    return {
      index,
      name: p.name,
      summary: dotJoin([
        byWeight ? `${n(d)} g → ${n(goalOf(p.r, d))} g` : `${n(d)} g`,
        byWeight ? '' : `${n(p.s)} s`,
        byWeight ? '' : `1:${formatNumber(p.r, 1, ctx.lang)}`,
        p.temp ? `${n(p.temp)} °C` : '',
        p.pi < 0 ? '' : `${ctx.tr('sPreinf')} ${ctx.tr(p.pi ? 'on' : 'off')}`,
      ]),
      tag: index === last ? ctx.tr('lastUsed') : null,
    };
  });
}
